import React, { useEffect, useState, useMemo, useRef } from "react";
import "./dashboard.scss";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { updateNewAssessmentVariable } from "../../../redux/slices/globalVariableSlice";
import Loading from "../../layout/loader/Loading";
import Pagination from "../../layout/pagination/Pagination";
import axiosInstance from "../../../httpInterceptor";
import { makeStyles } from "@mui/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TableSortLabel,
  IconButton,
  Menu,
  MenuItem,
} from "@material-ui/core";
import { FirstPage, LastPage, FilterList } from "@mui/icons-material";
import { FaSort as FaSort6 } from "react-icons/fa6";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import Tooltip, { TooltipProps } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

const useStyles = makeStyles({
  pagination: {
    "& .MuiTablePagination-toolbar": {
      backgroundColor: "white",
    },
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
      color: "#333",
    },
    "& .MuiTablePagination-actions": {
      "& .MuiButtonBase-root": {
        color: "#007bff",
      },
    },
  },
  root: {
    color: "white",
    "&:hover": {
      color: "white",
    },
    "&.MuiTableSortLabel-active": {
      color: "white",
    },
  },
  icon: {
    color: "white !important",
  },
  tableCell: {
    borderBottom: "none",
    borderTop: "none",
  },
  customBorder: {
    borderBottom: "1px solid transparent",
    border: "0",
  },
  tableRow: {
    "& td, & th": {
      border: "0 px",
      borderBottom: "none",
    },
  },
});

const CustomTooltip = styled(
  ({ className, title, children, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} title={title}>
      {children}
    </Tooltip>
  )
)({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: "white",
    color: "#074173",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
    fontSize: "14px",
  },
  [`& .MuiTooltip-arrow`]: {
    color: "white",
  },
});

const MyTooltip = styled(
  ({ className, title, children, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} title={title}>
      {children}
    </Tooltip>
  )
)({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: "white",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
    fontSize: "14px",
  },
});

const CustomPaginationActions = (props: any) => {
  const { count, page, rowsPerPage, onPageChange } = props;
  const handleFirstPageButtonClick = (event: any) => {
    onPageChange(event, 0);
  };
  const handleLastPageButtonClick = (event: any) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };
  return (
    <div style={{ flexShrink: 0, marginLeft: "2.5em" }}>
      <Button
        color="primary"
        style={{
          backgroundColor: "#074173",
          border: "1px solid grey",
          translate: "-15px",
          width: "6rem",
        }}
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        <FirstPage style={{ color: "white" }} />
      </Button>
      <Button
        color="primary"
        style={{
          backgroundColor: "#074173",
          translate: "-5px",
          border: "1px solid grey",
          width: "6rem",
        }}
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPage style={{ color: "white" }} />
      </Button>
    </div>
  );
};

const ProjectReadinessDashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const classes = useStyles();
  
  // Shared inline styles for filter dropdown (matching Dashboard.tsx)
  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    maxHeight: '14rem',
    overflowY: 'auto',
    width: '12rem',
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 6px 18px rgba(2,12,27,0.12)',
    zIndex: 1000,
    transform: 'translate(0px, 60px)',
    scrollbarWidth: 'thin',
    fontSize: '0.9rem',
    color: '#074173',
  };

  const menuItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#074173',
  };

  const checkboxStyle: React.CSSProperties = {
    width: '14px',
    height: '14px',
    accentColor: '#074173',
    cursor: 'pointer',
  };

  const [loading, setLoading] = useState(true);
  const [readinessData, setReadinessData] = useState<any[]>([]);
  const [error, setError] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState("assessment_date");
  const [query, setQuery] = useState({
    projectName: "",
    du: "",
    accountName: "",
    assessorName: "",
    assessmentType: "",
    assessment_date: "",
    healthStatus: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [projectsPerPage, setProjectsPerPage] = useState(5);

  // Assessment Date filter state
  const [selectedAssessmentDate, setSelectedAssessmentDate] = useState<string[]>([]);
  const [expandedAssessmentYears, setExpandedAssessmentYears] = useState<string[]>([]);
  const [expandedAssessmentMonths, setExpandedAssessmentMonths] = useState<{ [year: string]: string[] }>({});
  const [anchorElAssessment, setAnchorElAssessment] = useState<null | HTMLElement>(null);
  const filterMenuRefAssessment = useRef<HTMLDivElement>(null);
  const ignoreSortClickRef = useRef(false);

  const handlequerychange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery({
      ...query,
      [e.target.name]: e.target.value,
    });
  };

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setProjectsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  // Helper function to format assessment date to MM-DD-YYYY
  const formatAssessmentDate = (dateStr: any) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  // Helper function to group dates by year and month
  const groupDatesByYear = (dates: string[]) => {
    const map: { [year: string]: { [month: string]: string[] } } = {};
    dates.forEach((date) => {
      const parts = date.split('-');
      if (parts.length !== 3) return;
      const [mm, dd, yyyy] = parts;
      if (!yyyy) return;
      if (!map[yyyy]) map[yyyy] = {};
      if (!map[yyyy][mm]) map[yyyy][mm] = [];
      map[yyyy][mm].push(date);
    });
    return map;
  };

  // Convert month number to name
  const monthNumberToName = (mm: string) => {
    const mapping: { [key: string]: string } = {
      '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr',
      '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Aug',
      '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec',
    };
    const padded = mm.padStart(2, '0');
    return mapping[padded] ?? mm;
  };

  const normalizeToMMDDYYYY = (dateStr: string) => {
    if (!dateStr) return dateStr;
    const parts = String(dateStr).split('-');
    if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 4) return dateStr;
    return dateStr;
  };

  // Assessment date options grouped by year - memoized to prevent recalculation
  const assessmentDateOptions = useMemo(() => {
    return Array.from(
      new Set(
        readinessData
          .map((item) => item.assessment_date)
          .filter((date) => date)
          .map((date) => formatAssessmentDate(date))
      )
    ).sort();
  }, [readinessData]);

  const assessmentDatesByYear = useMemo(() => {
    return groupDatesByYear(assessmentDateOptions);
  }, [assessmentDateOptions]);

  // Toggle year expansion
  const toggleAssessmentYearExpansion = (year: string) => {
    setExpandedAssessmentYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );
  };

  // Toggle month expansion
  const toggleAssessmentMonthExpansion = (year: string, month: string) => {
    setExpandedAssessmentMonths((prev) => {
      const months = prev[year] ?? [];
      const exists = months.includes(month);
      return {
        ...prev,
        [year]: exists ? months.filter((m) => m !== month) : [...months, month],
      };
    });
  };

  // Toggle year selection
  const toggleAssessmentYearSelection = (year: string, datesInYear: string[]) => {
    const allSelected = datesInYear.every((date) => selectedAssessmentDate.includes(date));
    if (allSelected) {
      setSelectedAssessmentDate((prev) => prev.filter((date) => !datesInYear.includes(date)));
    } else {
      setSelectedAssessmentDate((prev) => {
        const newDates = datesInYear.filter((d) => !prev.includes(d));
        return [...prev, ...newDates];
      });
    }
  };

  // Toggle month selection
  const toggleAssessmentMonthSelection = (year: string, month: string, datesInMonth: string[]) => {
    const allSelected = datesInMonth.every((d) => selectedAssessmentDate.includes(d));
    if (allSelected) {
      setSelectedAssessmentDate((prev) => prev.filter((d) => !datesInMonth.includes(d)));
    } else {
      setSelectedAssessmentDate((prev) => {
        const newDates = datesInMonth.filter((d) => !prev.includes(d));
        return [...prev, ...newDates];
      });
    }
  };

  // Toggle individual date selection
  const toggleAssessmentDateSelection = (date: string) => {
    setSelectedAssessmentDate((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  };

  // Select/deselect all assessment dates
  const toggleSelectAllAssessmentDates = () => {
    const allDates = Object.values(assessmentDatesByYear).flatMap((m) => Object.values(m)).flat();
    if (allDates.length === 0) return;
    const flat = allDates.map((d) => String(d));
    const allSelected = flat.every((d) => selectedAssessmentDate.includes(d));
    if (allSelected) setSelectedAssessmentDate([]);
    else setSelectedAssessmentDate(flat);
  };

  // Assessment filter menu handlers
  const handleClickAssessment = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorElAssessment(anchorElAssessment ? null : event.currentTarget);
  };
  const handleCloseAssessment = () => {
    setAnchorElAssessment(null);
  };

  // Click outside handler to close assessment filter menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterMenuRefAssessment.current &&
        !filterMenuRefAssessment.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('[data-filter-button="assessmentDate"]')
      ) {
        setAnchorElAssessment(null);
        ignoreSortClickRef.current = true;
        setTimeout(() => { ignoreSortClickRef.current = false; }, 300);
      }
    };

    if (anchorElAssessment) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorElAssessment]);

  // getSortIcon function similar to Dashboard.tsx
  const getSortIcon = (property: any) => {
    const isActive = orderBy === property;
    const iconType = !isActive ? 'neutral' : (order === 'asc' ? 'up' : 'down');

    // Custom sort icon with separate hover areas for up and down arrows with tooltips
    return (
      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', width: '14px', height: '14px' }}>
        <FaSort6 style={{ color: '#000', fontSize: '0.80rem', pointerEvents: 'none' }} />
        <Tooltip title="Click to sort ascending" placement="top" arrow>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '50%',
              cursor: 'pointer',
              zIndex: 2,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleRequestSort(property);
            }}
          />
        </Tooltip>
        <Tooltip title="Click to sort descending" placement="bottom" arrow>
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '50%',
              cursor: 'pointer',
              zIndex: 2,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleRequestSort(property);
            }}
          />
        </Tooltip>
      </div>
    );
  };

  const handleRequestSort = (property: string) => {
    if (ignoreSortClickRef.current) {
      return;
    }
    console.log(`[BEFORE CLICK] orderBy: ${orderBy}, order: ${order}, property: ${property}`);
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    console.log(`[AFTER CALCULATION] isAsc: ${isAsc}, newOrder: ${newOrder}`);
    setOrder(() => {
      console.log(`[SET ORDER] Setting to: ${newOrder}`);
      return newOrder;
    });
    setOrderBy(() => {
      console.log(`[SET ORDERBY] Setting to: ${property}`);
      return property;
    });
    setCurrentPage(1);
  };

  // Fetch readiness data from API
  const fetchReadinessData = async () => {
    try {
      setLoading(true);
      
      // Call the GET /preprojects API to fetch all preproject readiness records
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_PRE_PROJECT_READINESS_SERVICE_BASE_URL}/preprojects`
      );
      
      const rawData = response.data.items || [];
      
      // Transform backend data to match dashboard format
      const transformedData = rawData.map((item: any) => {
        return {
          id: item.assessmentId || item._id,
          preprojectId: item.preprojectId,
          projectName: item.projectName || "N/A",
          du: item.duName || "N/A",
          accountName: item.accountName || "N/A",
          assessorName: item.assessorName || "N/A",
          assessmentType: item.setName ? String(item.setName).trim() : "N/A",
          healthStatus: "N/A",
          assessment_date: item.createdAt || new Date().toISOString(),
          created_date: item.createdAt,
          projectDescription: item.projectDescription || "",
          set_name: item.setName ? String(item.setName).trim() : "",
        };
      });

      // Fetch health statuses by looking up assessments via preprojectId + setName
      // (The preproject record's assessmentId does NOT match the assessment-readiness-service's assessmentId,
      //  so we use createPreprojectAssessment as a find-or-create lookup — same pattern as Question.tsx)
      const itemsWithPreprojectId = transformedData.filter(
        (item: any) => item.preprojectId && item.set_name
      );
      if (itemsWithPreprojectId.length > 0) {
        const BATCH_SIZE = 10;
        for (let i = 0; i < itemsWithPreprojectId.length; i += BATCH_SIZE) {
          const batch = itemsWithPreprojectId.slice(i, i + BATCH_SIZE);
          await Promise.all(
            batch.map(async (item: any) => {
              try {
                const response = await axiosInstance.post(
                  `${process.env.REACT_APP_READINESS_SERVICE_BASE_URL}/createPreprojectAssessment`,
                  {
                    preprojectId: item.preprojectId,
                    setName: item.set_name,
                    emailId: [],
                    assessmentDetails: [],
                  }
                );
                const details = response.data?.assessmentDetails || [];
                if (details.length > 0) {
                  const lastDetail = details[details.length - 1];
                  item.healthStatus = lastDetail.projectstatus || "N/A";
                }
              } catch {
                // Keep healthStatus as "N/A"
              }
            })
          );
        }
      }
      
      // Initialize default selections with all dates
      const allDates = transformedData
        .map((item: any) => formatAssessmentDate(item.assessment_date))
        .filter((d: string) => d && d !== "N/A");
      
      // Batch state updates using React 18's automatic batching
      // All state updates will be batched together in a single re-render
      setReadinessData(transformedData);
      if (allDates.length > 0) {
        setSelectedAssessmentDate(allDates);
      }
      setLoading(false);
    } catch (error: any) {
      console.error("Error fetching readiness data:", error);
      setError(error.message || "Failed to fetch readiness data");
      setLoading(false);
    }
  };

  const handleNewAssessmentClick = () => {
    navigate("/dashboard/create-project", { state: { showProjectReadiness: true } });
  };

  const getHealthStatusIcon = (status: string) => {
    const h = String(status || "").toLowerCase().trim();
    
    
    if ( h === "green") {
      return (
        <MyTooltip placement="top" title={<span style={{ color: "#16a34a" }}>Project Health is Green</span>}>
          <div className="health-chip healthy">
            <span className="dot" style={{translate:'0px'}}>✓</span>
          </div>
        </MyTooltip>
      );
    }
    if (h === "amber" || h === "yellow") {
      return (
        <MyTooltip placement="top" title={<span style={{ color: "#f59e0b" }}>Project Health is Amber</span>}>
          <div className="health-chip warning">
            <span className="dot"  style={{translate:'0px'}}>!</span>
          </div>
        </MyTooltip>
      );
    } 
    if (h === "red") {
      return (
        <MyTooltip placement="top" title={<span style={{ color: "red" }}>Project Health is Red</span>}>
          <div className="health-chip critical">
            <span className="dot"  style={{translate:'0px'}}>X</span>
          </div>
        </MyTooltip>
      );
    }
    // Handle N/A status or empty status - show gray neutral indicator
    return (
      <MyTooltip placement="top" title={<span style={{ color: "#6b7280" }}>Health Status N/A</span>}>
        <div style={{
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          backgroundColor: "#d1d5db",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "14px",
          fontWeight: "bold",
          color: 'white',
          translate:'5.8rem'
        }}>–</div>
      </MyTooltip>
    );
  };

  useEffect(() => {
    fetchReadinessData();
  }, []);

  // Filter data based on search queries and selected assessment dates using useMemo
  const filteredData = useMemo(() => {
    // Early return if no data to filter
    if (readinessData.length === 0) {
      return [];
    }

    let filtered = readinessData;

    // Apply assessment date menu filter first
    if (selectedAssessmentDate && selectedAssessmentDate.length === 0) {
      return [];
    }
    if (selectedAssessmentDate && selectedAssessmentDate.length > 0) {
      filtered = filtered.filter((item) => {
        if (!item.assessment_date) return false;
        const formattedDate = formatAssessmentDate(item.assessment_date);
        return selectedAssessmentDate.includes(formattedDate);
      });
    }

    // Apply text search filters
    if (query.projectName) {
      filtered = filtered.filter((item) =>
        item.projectName?.toLowerCase().includes(query.projectName.toLowerCase())
      );
    }
    if (query.du) {
      filtered = filtered.filter((item) =>
        item.du?.toLowerCase().includes(query.du.toLowerCase())
      );
    }
    if (query.accountName) {
      filtered = filtered.filter((item) =>
        item.accountName?.toLowerCase().includes(query.accountName.toLowerCase())
      );
    }
    if (query.assessorName) {
      filtered = filtered.filter((item) =>
        item.assessorName?.toLowerCase().includes(query.assessorName.toLowerCase())
      );
    }
    if (query.assessmentType) {
      filtered = filtered.filter((item) =>
        item.assessmentType?.toLowerCase().includes(query.assessmentType.toLowerCase())
      );
    }
    if (query.assessment_date) {
      const searchTerm = query.assessment_date.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const itemDate = item.assessment_date;
        if (searchTerm === 'n/a' || searchTerm === 'na' || searchTerm === 'n') {
          if (!itemDate) return true;
          const formattedDate = formatAssessmentDate(itemDate);
          return formattedDate === "N/A";
        }
        if (!itemDate) return false;
        const formattedItemDate = formatAssessmentDate(itemDate);
        if (formattedItemDate === "N/A") return false;
        const dateParts = formattedItemDate.split("-");
        if (searchTerm.includes("-")) {
          return formattedItemDate.includes(searchTerm);
        } else {
          const paddedSearch = searchTerm.padStart(2, '0');
          if (searchTerm.length <= 2) {
            const matchesDayOrMonth = dateParts[0] === paddedSearch || dateParts[1] === paddedSearch;
            if (matchesDayOrMonth) return true;
            const lastTwoDigitsOfYear = dateParts[2].slice(-2);
            if (lastTwoDigitsOfYear === paddedSearch) return true;
          }
          return dateParts[2].startsWith(searchTerm);
        }
      });
    }
    if (query.healthStatus) {
      const rawSearch = String(query.healthStatus).toLowerCase().trim();
      const normalizedSearch = rawSearch.replace(/[^a-z0-9]/g, "");
      const normalizeHealth = (v: any) => String(v ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
      if (normalizedSearch === "na" || normalizedSearch === "n") {
        filtered = filtered.filter((item: any) => {
          const h = normalizeHealth(item.healthStatus);
          return h === "" || h === "na";
        });
      } else {
        filtered = filtered.filter((item: any) => {
          const h = normalizeHealth(item.healthStatus);
          if (!h) return false;
          return h.includes(normalizedSearch) || normalizedSearch.includes(h);
        });
      }
    }

    return filtered;
  }, [query, readinessData, selectedAssessmentDate]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredData]);

  // Sort data (matching Dashboard.tsx logic)
  const sortedData = useMemo(() => {
    console.log(`[SORTING TRIGGERED] orderBy: ${orderBy}, order: ${order}, items: ${filteredData.length}`);
    return [...filteredData].sort((a, b) => {
      // Health sorting
      if (orderBy === "healthStatus") {
        const normalizeHealth = (v: any) => String(v ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const healthA = normalizeHealth(a.healthStatus);
        const healthB = normalizeHealth(b.healthStatus);

        const isNA_A = healthA === "na" || healthA === "";
        const isNA_B = healthB === "na" || healthB === "";
        if (isNA_A && isNA_B) return 0;
        if (isNA_A) return 1;
        if (isNA_B) return -1;

        // Ascending order: Red (1) -> Amber (2) -> Green (3)
        // Descending order: Green (1) -> Amber (2) -> Red (3)
        const healthPriority: { [key: string]: number } = {
          red: 1,
          amber: 2,
          green: 3
        };
        const priorityA = healthPriority[healthA] ?? 999;
        const priorityB = healthPriority[healthB] ?? 999;

        const result = order === "asc" ? priorityA - priorityB : priorityB - priorityA;
        return result;
      }

      if (orderBy === "assessment_date") {
        const rawA = a[orderBy];
        const rawB = b[orderBy];

        // Prefer the full timestamp (raw value) so that
        // assessments on the same day but added later (later time) appear first.
        const timeAraw = rawA ? new Date(rawA).getTime() : NaN;
        const timeBraw = rawB ? new Date(rawB).getTime() : NaN;

        let validAraw = !isNaN(timeAraw);
        let validBraw = !isNaN(timeBraw);

        // If raw parse failed, try falling back to the formatted display date
        let timeA = timeAraw;
        let timeB = timeBraw;
        if (!validAraw) {
          const fa = rawA ? formatAssessmentDate(rawA) : "N/A";
          timeA = new Date(fa).getTime();
          validAraw = !isNaN(timeA);
        }
        if (!validBraw) {
          const fb = rawB ? formatAssessmentDate(rawB) : "N/A";
          timeB = new Date(fb).getTime();
          validBraw = !isNaN(timeB);
        }

        // Place invalid/N/A at the end
        if (!validAraw && !validBraw) return 0;
        if (!validAraw) return 1;
        if (!validBraw) return -1;

        // If timestamps are equal, fall back to stable tie-breaker using id
        if (timeA === timeB) {
          const idA = a.id ?? "";
          const idB = b.id ?? "";
          return order === "asc" ? String(idA).localeCompare(String(idB)) : String(idB).localeCompare(String(idA));
        }

        return order === "asc" ? timeA - timeB : timeB - timeA;
      }

      return order === "asc"
        ? a[orderBy]?.localeCompare(b[orderBy])
        : b[orderBy]?.localeCompare(a[orderBy]);
    });
  }, [filteredData, orderBy, order]);

  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;

  const paginatedData = useMemo(() => {
    return sortedData.slice(
      indexOfFirstProject,
      indexOfLastProject
    );
  }, [sortedData, indexOfFirstProject, indexOfLastProject]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="error">
        <h3>Something went wrong!</h3>
      </div>
    );
  }

  return (
    <>
      <div style={{ marginTop: "-95px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end", marginBottom: "0px" }}>
          <div style={{ display: "flex", gap: "10px", marginRight: "10px" }}>
            <button
              onClick={handleNewAssessmentClick}
              style={{
                height: "36px",
                fontSize: "0.85rem",
                background: "linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)",
                borderRadius: "6px",
                color: "#ffffff",
                border: "none",
                boxShadow: "0 1px 4px rgba(13,94,255,0.18)",
                padding: "0 12px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
              }}
            >
              <span>Log</span>
              <span>Readiness</span>
              <span>Assessment</span>
              <span style={{ marginLeft: "5px" }}>+</span>
            </button>
          </div>
          <div style={{ 
            display: "flex", 
            gap: "16px", 
            alignItems: "center",
            fontSize: "0.85rem",
            marginRight: "10px",
            marginBottom: "15px",
            marginTop: "5px",
            translate:'0px -10px'
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ 
                width: "20px", 
                height: "20px", 
                borderRadius: "50%", 
                backgroundColor: "#4caf50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "14px",
                fontWeight: "bold"
              }}>✓</div>
              <span style={{ color: "#000", fontWeight: 500 }}>Green</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ 
                width: "20px", 
                height: "20px", 
                borderRadius: "50%", 
                backgroundColor: "#ef4444",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "14px",
                fontWeight: "bold"
              }}>✕</div>
              <span style={{ color: "#000", fontWeight: 500 }}>Red</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ 
                width: "20px", 
                height: "20px", 
                borderRadius: "50%", 
                backgroundColor: "#f59e0b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "16px",
                fontWeight: "bold"
              }}>!</div>
              <span style={{ color: "#000", fontWeight: 500 }}>Amber</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                backgroundColor: "#d1d5db",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                fontWeight: "bold",
                color:'white',
                textAlign:'center',
              }}>–</div>
              <span style={{ color: "#000", fontWeight: 500 }}>N/A</span>
            </div>
          </div>
        </div>

        {readinessData.length > 0 ? (
          <div
            className="projectlistTableContainer"
            style={{ marginTop: "-20px" }}
          >
            <div className="projectlistTableContainer__wrapper">
              <table className="tstylee">
                <thead>
                  <TableRow
                    style={{
                      borderTopLeftRadius: "0px",
                    }}
                  >
                  {[
                    { id: "projectName", label: <span style={{ translate: '0px -8px' }}>Project Name</span> },
                    { id: "du", label: <span style={{ translate: '0px -8px' }}>DU</span> },
                    { id: "accountName", label: <span style={{ translate: '0px -8px' }}>Account Name</span> },
                    { id: "assessmentType", label: <span style={{ translate: '0px -8px', whiteSpace:'nowrap' }}>Assessment Type</span> },
                    { 
                      id: "assessment_date", 
                      label: (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                          <span style={{ whiteSpace: 'nowrap' }}>Assessment Date</span>
                          <span style={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}>(MM-DD-YYYY)</span>
                          <div>
                            <IconButton
                              style={{ background: "transparent", padding: '2px' }}
                              onClick={handleClickAssessment}
                              data-filter-button="assessmentDate"
                            >
                              <CustomTooltip title="Filter">
                                <FilterList style={{ color: "black", fontSize: '1rem' }} />
                              </CustomTooltip>
                            </IconButton>

                            <Menu
                              id="assessment-filter-menu"
                              anchorEl={anchorElAssessment}
                              open={Boolean(anchorElAssessment)}
                              onClose={handleCloseAssessment}
                              getContentAnchorEl={null}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                              MenuListProps={{ onMouseDown: (e: any) => e.stopPropagation(), onClick: (e: any) => e.stopPropagation() }}
                              PaperProps={{ ref: filterMenuRefAssessment, style: { width: '12rem', maxHeight: '14rem', overflowY: 'auto', zIndex: 2000 } }}
                              style={{translate:"57rem 13rem"}}
                            >
                              <div
                                className="custom-menu-item"
                                style={{ ...menuItemStyle, fontStyle: 'normal', justifyContent: 'flex-start' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  toggleSelectAllAssessmentDates();
                                }}
                              >
                                <input type="checkbox" checked={(() => { const all = Object.values(assessmentDatesByYear).flatMap((m)=>Object.values(m)).flat(); return all.length>0 && all.every((d)=> selectedAssessmentDate.includes(String(d))) })()} readOnly style={checkboxStyle} />
                                <span style={{ marginLeft: 3 }}>All</span>
                              </div>
                              {Object.keys(assessmentDatesByYear)
                                .sort((a, b) => Number(b) - Number(a))
                                .map((year) => {
                                const monthsObj = assessmentDatesByYear[year];
                                const datesInYearFlat = Object.values(monthsObj).flat();
                                const allSelected = datesInYearFlat.every((date) => selectedAssessmentDate.includes(date));
                                const someSelected = datesInYearFlat.some((date) => selectedAssessmentDate.includes(date));
                                return (
                                <div key={year}>
                                  <div
                                    className="custom-menu-item"
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggleAssessmentYearExpansion(year);
                                      }
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleAssessmentYearExpansion(year);
                                    }}
                                    style={{ 
                                      ...menuItemStyle,
                                      fontWeight: 'bold',
                                      cursor: 'pointer',
                                      justifyContent: 'space-between'
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={(input) => {
                                          if (input) {
                                            input.indeterminate = someSelected && !allSelected;
                                          }
                                        }}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          toggleAssessmentYearSelection(year, datesInYearFlat);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        style={checkboxStyle}
                                      />
                                      <span>{year}</span>
                                    </div>
                                    <span 
                                      style={{ width: '12px', flexShrink: 0, fontSize: '0.65rem', lineHeight: '1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                      {expandedAssessmentYears.includes(year) ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                                    </span>
                                  </div>
                                  {expandedAssessmentYears.includes(year) && (
                                    <div style={{ paddingLeft: '24px' }}>
                                      {Object.keys(monthsObj).sort((a,b)=>Number(a)-Number(b)).map((mm) => {
                                        const datesInMonth = monthsObj[mm].sort((a,b)=> new Date(b).getTime() - new Date(a).getTime());
                                        const monthLabel = monthNumberToName(mm);
                                        const monthExpanded = (expandedAssessmentMonths[year] || []).includes(mm);
                                        const allMonthSelected = datesInMonth.every(d=> selectedAssessmentDate.includes(d));
                                        const someMonthSelected = datesInMonth.some(d=> selectedAssessmentDate.includes(d));
                                        return (
                                          <div key={`${year}-${mm}`}>
                                            <div className="custom-menu-item" style={{ ...menuItemStyle, justifyContent:'space-between' }}
                                              role="button"
                                              tabIndex={0}
                                              onKeyDown={(e)=>{
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  toggleAssessmentMonthExpansion(year, mm);
                                                }
                                              }}
                                              onClick={(e)=>{ e.stopPropagation(); toggleAssessmentMonthExpansion(year, mm); }}
                                            >
                                              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                                <input
                                                  type="checkbox"
                                                  checked={allMonthSelected}
                                                  ref={(input)=>{ if(input){ input.indeterminate = someMonthSelected && !allMonthSelected } }}
                                                  onChange={(e)=>{ e.stopPropagation(); toggleAssessmentMonthSelection(year, mm, datesInMonth); }}
                                                  onClick={(e)=> e.stopPropagation()}
                                                  style={checkboxStyle}
                                                />
                                                <span>{monthLabel}</span>
                                              </div>
                                              <span style={{ width:'12px', flexShrink:0, fontSize: '0.65rem', lineHeight: '1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{ monthExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} /> }</span>
                                            </div>
                                            { monthExpanded && (
                                              <div style={{ paddingLeft: '20px' }}>
                                                {datesInMonth.map((date)=> (
                                                  <div key={date} className="custom-menu-item" onClick={(e)=>{ e.stopPropagation(); toggleAssessmentDateSelection(date); }} style={{ ...menuItemStyle, whiteSpace:'nowrap' }}>
                                                    <input type="checkbox" checked={selectedAssessmentDate.includes(date)} readOnly style={checkboxStyle} />
                                                    <span style={{ whiteSpace:'nowrap' }}>{normalizeToMMDDYYYY(date)}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            ) }
                                          </div>
                                        )
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            </Menu>
                          </div>
                        </div>
                      )
                    },
                    { id: "healthStatus", label: <span style={{ translate: '0px -8px' }}>Health</span> },
                  ].map((header) => (
                    <TableCell
                      key={header.id}
                      style={{
                        fontSize: "0.8rem",
                        color: "black",
                        padding: "8px 6px",
                        textAlign: "center",
                        backgroundColor: "#D3D3D3",
                        verticalAlign: "middle",
                        lineHeight: "1.2",
                        height: "auto",
                        minWidth: "fit-content",
                        fontWeight: "600",
                        letterSpacing: "0.3px",
                      }}
                      sortDirection={orderBy === header.id ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === header.id}
                        style={{ 
                          textAlign: "center", 
                          cursor: "default",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: "auto",
                          height: "auto",
                        }}
                        direction={orderBy === header.id ? order : "asc"}
                        classes={{
                          root: classes.root,
                          icon: classes.icon,
                        }}
                        IconComponent={() => null}
                      >
                        <span
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            color: "black",
                            fontWeight: "bold",
                            display: "inline-flex",
                            alignItems: "center",
                            verticalAlign: "middle",
                            lineHeight: "1.1",
                            cursor: 'default'
                          }}
                          className="header-cell"
                        >
                          {header.label}{" "}
                          <span
                            key={`${header.id}-${orderBy}-${order}`}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              marginLeft: "4px",
                              verticalAlign: "middle",
                              translate: ["projectName", "du", "accountName", "assessmentType", "assessment_date", "healthStatus"].includes(header.id) ? "0px -8px" : undefined,
                            }}
                            className="sort-icon"
                          >
                            {getSortIcon(header.id)}
                          </span>
                        </span>
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow
                  style={{
                    height: "12px",
                    borderBottom: "1px solid #e2e8f0",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <td className="inputContainer2" style={{ backgroundColor: "#f8fafc" }}>
                    <input
                      type="text"
                      name="projectName"
                      onChange={handlequerychange}
                      style={{ height: "30px" }}
                      placeholder="Search"
                      aria-label="Search project name"
                    />
                    {query.projectName.length === 0 && (
                      <i className="fa-solid fa-magnifying-glass"></i>
                    )}
                  </td>
                  <td className="inputContainer2" style={{ backgroundColor: "#f8fafc" }}>
                    <input
                      type="text"
                      name="du"
                      onChange={handlequerychange}
                      style={{ height: "30px" }}
                      placeholder="Search"
                      aria-label="Search DU"
                    />
                    {query.du.length === 0 && (
                      <i className="fa-solid fa-magnifying-glass"></i>
                    )}
                  </td>
                  <td className="inputContainer2" style={{ backgroundColor: "#f8fafc" }}>
                    <input
                      type="text"
                      name="accountName"
                      onChange={handlequerychange}
                      style={{ height: "30px" }}
                      placeholder="Search"
                      aria-label="Search account"
                    />
                    {query.accountName.length === 0 && (
                      <i className="fa-solid fa-magnifying-glass"></i>
                    )}
                  </td>
                  <td className="inputContainer2" style={{ backgroundColor: "#f8fafc" }}>
                    <input
                      type="text"
                      name="assessmentType"
                      onChange={handlequerychange}
                      style={{ height: "30px" }}
                      placeholder="Search"
                      aria-label="Search assessment type"
                    />
                    {query.assessmentType.length === 0 && (
                      <i className="fa-solid fa-magnifying-glass"></i>
                    )}
                  </td>
                  <td className="inputContainer2" style={{ backgroundColor: "#f8fafc" }}>
                    <input
                      type="text"
                      name="assessment_date"
                      onChange={handlequerychange}
                      style={{ height: "30px" }}
                      placeholder="Search"
                      aria-label="Search assessment date"
                    />
                    {query.assessment_date.length === 0 && (
                      <i className="fa-solid fa-magnifying-glass"></i>
                    )}
                  </td>
                  <td className="inputContainer2" style={{ backgroundColor: "#f8fafc" }}>
                    <input
                      type="text"
                      name="healthStatus"
                      onChange={handlequerychange}
                      style={{ height: "30px" }}
                      placeholder="Search"
                      aria-label="Search health"
                    />
                    {query.healthStatus.length === 0 && (
                      <i className="fa-solid fa-magnifying-glass"></i>
                    )}
                  </td>
                </TableRow>
              </thead>

              <tbody key={`tbody-${orderBy}-${order}-${currentPage}`} style={{ backgroundColor: "#ffffff" }}>
                {paginatedData.length === 0 ? (
                  <tr
                    style={{
                      overflow: "hidden",
                      height: "auto",
                      minHeight: "80px",
                    }}
                  >
                    <td
                      colSpan={6}
                      className="nodata"
                      style={{
                        backgroundColor: "#f8fafc",
                        color: "#64748b",
                        fontSize: "1rem",
                        fontWeight: "600",
                        textAlign:'center',
                        padding: "40px 20px",
                        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                      }}
                    >
                      No Record Found
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row: any, index: number) => (
                    <tr
                      key={`row-${indexOfFirstProject + index}-${row.id}`}
                      style={{
                        height: "40px",
                        overflow: "auto",
                        maxHeight: "60px",
                        borderBottom: "1px solid white",
                      }}
                    >
                      <td
                        style={{
                          paddingLeft: "0.5%",
                          textAlign: "center",
                        }}
                      >
                        <CustomTooltip title={row.projectName}>
                          <Link
                            style={{
                              color: " #074173",
                              fontFamily: "system-ui",
                              cursor: "pointer",
                            }}
                          to={`/dashboard/re-assessment/${row.preprojectId}/${encodeURIComponent(row.set_name)}`}
                            state={{ 
                              fromProjectReadiness: true,
                              readinessData: {
                                assessmentId: row.id,
                                projectName: row.projectName,
                                preprojectId: row.preprojectId,
                                du: row.du,
                                accountName: row.accountName,
                                assessorName: row.assessorName,
                                assessmentType: row.assessmentType,
                                set_name: row.set_name,
                                projectDescription: row.projectDescription || "",
                              }
                            }}
                            onClick={() => {
                              dispatch(updateNewAssessmentVariable(false));
                            }}
                          >
                            <span style={{ wordWrap: "break-word" }}>
                              {row.projectName || "N/A"}
                            </span>
                          </Link>
                        </CustomTooltip>
                      </td>

                      <td
                        style={{
                          paddingLeft: "0.5%",
                          textAlign: "center",
                        }}
                      >
                        {row.du || "N/A"}
                      </td>

                      <td
                        style={{
                          paddingLeft: "0.5%",
                          textOverflow: "ellipsis",
                          textAlign: "center",
                        }}
                      >
                        <CustomTooltip title={row.accountName}>
                          <span>{row.accountName || "N/A"}</span>
                        </CustomTooltip> 
                      </td>

                      <td
                        style={{
                          textAlign: "center",
                        }}
                      >
                        {row.assessmentType || "N/A"}
                      </td>

                      <td style={{ paddingRight: "0.5%", textAlign: "center" }}>
                        {formatAssessmentDate(row.assessment_date)}
                      </td>

                      <td style={{ paddingLeft: "0.5%", textAlign: "center"}}>
                        {getHealthStatusIcon(row.healthStatus)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>

            {/* pagination component - fixed at bottom */}
            <div className="projectlistTableContainer__pagination">
              <Pagination
                projectsPerPage={projectsPerPage}
                totalProjects={filteredData.length}
                paginate={paginate}
                currentPage={currentPage}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </div>
          </div>
        ) : (
          // message when there is no readiness assessment
          <div className="assessmentNotfound">
            No Readiness Assessment Found!
          </div>
        )}
      </div>
    </>
  );
};

export default ProjectReadinessDashboard;
